import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { firebaseAuth } from "./firebase";
import { httpRequestJson, isIosCapacitor } from "./utils/nativeHttp";
import { persistManualAuth } from "./utils/manualAuth";

// LOOP AUTH CODE HTTP IOS RELIABILITY V29 — resend/verify code uses native-safe HTTP with real timeout and iOS session persistence.

const LOOP_AUTH_FUNCTIONS_BASE = "https://us-central1-web-app-d9799.cloudfunctions.net";
const LOOP_RESEND_CODE_HTTP_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/resendVerificationCodeHttpV29`;
const LOOP_VERIFY_CODE_HTTP_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/verifyRegistrationCodeHttpV29`;
const LOOP_FIREBASE_API_KEY = "AIzaSyAX2UN-Iqn2LIk2nftv8evqCibnsZfH8qc";

function withTimeout(promise, ms, label = "timeout") {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try { if (timer) clearTimeout(timer); } catch (_) {}
    }),
    timeout,
  ]);
}

function codeFlowErrorMessage(err, fallback = "No pudimos completar la acción. Intenta nuevamente.") {
  const raw = String(err?.message || err?.code || err || "").toLowerCase();
  if (raw.includes("deadline") || raw.includes("expired") || raw.includes("expir")) return "El código expiró. Solicita uno nuevo para continuar.";
  if (raw.includes("incorrect") || raw.includes("permission") || raw.includes("código incorrecto") || raw.includes("codigo incorrecto")) return "El código no coincide. Revisa el correo e intenta nuevamente.";
  if (raw.includes("too many") || raw.includes("demasiados") || raw.includes("resource-exhausted") || raw.includes("429")) return "Hiciste demasiados intentos. Espera unos minutos y solicita un nuevo código.";
  if (raw.includes("not-found") || raw.includes("registro pendiente")) return "No encontramos tu registro pendiente. Vuelve a crear tu cuenta.";
  if (raw.includes("timeout")) return "La conexión tardó demasiado. Revisa tu internet e intenta nuevamente.";
  if (raw.includes("network") || raw.includes("load failed")) return "No pudimos conectarnos. Revisa tu internet e intenta nuevamente.";
  if (raw.includes("mail") || raw.includes("email")) return "No pudimos enviar el correo. Revisa el email o intenta más tarde.";
  return fallback;
}

async function exchangeCustomTokenForIdToken(customToken) {
  if (!customToken) throw new Error("missing-custom-token");
  const result = await withTimeout(
    httpRequestJson({
      url: `https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken?key=${LOOP_FIREBASE_API_KEY}`,
      method: "POST",
      data: { token: customToken, returnSecureToken: true },
    }),
    25000,
    "exchange-custom-token-timeout"
  );
  if (!result?.idToken) throw new Error("missing-id-token-after-custom-token");
  return result;
}

export default function EnterCode() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const uidFromState = state.uid || null;
  const emailFromState = state.email || null;

  const [uid, setUid] = useState(uidFromState || window.localStorage.getItem("pending_uid") || "");
  const [email, setEmail] = useState(emailFromState || window.localStorage.getItem("pending_email") || "");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const inputRefs = useRef([]);
  const code = useMemo(() => digits.join(""), [digits]);
  const nextTarget = useMemo(() => {
    try {
      const fromState = typeof state.next === "string" ? state.next : "";
      const fromLocal = window.localStorage.getItem("loop_post_auth_redirect") || window.sessionStorage.getItem("loop_post_auth_redirect") || "";
      const target = fromState || fromLocal || "/";
      if (!target.startsWith("/") || target.startsWith("//")) return "/";
      if (["/login", "/register", "/signup", "/enter-code", "/verify-email", "/forgot-password", "/reset-password"].includes(target.split("?")[0])) return "/";
      return target;
    } catch (_) {
      return "/";
    }
  }, [state.next]);

  const finishVerifiedSession = async ({ customToken = "", verifiedEmail = "", displayName = "" } = {}) => {
    window.localStorage.removeItem("pending_uid");
    window.localStorage.removeItem("pending_email");

    try { window.localStorage.removeItem("loop_post_auth_redirect"); } catch (_) {}
    try { window.sessionStorage.removeItem("loop_post_auth_redirect"); } catch (_) {}

    if (customToken && isIosCapacitor()) {
      const tokenResult = await exchangeCustomTokenForIdToken(customToken);
      persistManualAuth({
        source: "ios-native-email-verification-v29",
        uid: tokenResult.localId || uid || null,
        email: tokenResult.email || verifiedEmail || email || "",
        displayName: displayName || "",
        idToken: tokenResult.idToken,
        refreshToken: tokenResult.refreshToken || "",
        expiresIn: tokenResult.expiresIn || "3600",
        obtainedAt: new Date().toISOString(),
        emailVerified: true,
      });
      navigate(nextTarget || "/", { replace: true });
      return;
    }

    if (customToken) {
      try {
        await signInWithCustomToken(firebaseAuth, customToken);
        navigate(nextTarget || "/", { replace: true });
        return;
      } catch (signErr) {
        console.warn("[EnterCode V29] signInWithCustomToken failed, sending user to login", signErr);
      }
    }

    navigate("/login", {
      replace: true,
      state: {
        info: "Tu correo fue verificado. Ahora puedes iniciar sesión.",
        email: verifiedEmail || email,
      },
    });
  };

  useEffect(() => {
    if (uidFromState) {
      window.localStorage.setItem("pending_uid", uidFromState);
      setUid(uidFromState);
    }
    if (emailFromState) {
      window.localStorage.setItem("pending_email", emailFromState);
      setEmail(emailFromState);
    }
  }, [uidFromState, emailFromState]);

  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 80);
  }, []);

  const maskedEmail = useMemo(() => {
    const raw = String(email || "").trim();
    if (!raw || !raw.includes("@")) return "";
    const [name, domain] = raw.split("@");
    const safeName = name.length <= 2 ? `${name.slice(0, 1)}***` : `${name.slice(0, 2)}***${name.slice(-1)}`;
    return `${safeName}@${domain}`;
  }, [email]);

  const updateDigit = (index, value) => {
    const clean = String(value || "").replace(/\D/g, "");

    if (clean.length > 1) {
      const next = ["", "", "", "", "", ""];
      clean.slice(0, 6).split("").forEach((char, i) => {
        next[i] = char;
      });
      setDigits(next);
      setTimeout(() => inputRefs.current[Math.min(clean.length, 6) - 1]?.focus(), 0);
      return;
    }

    setDigits((prev) => {
      const next = [...prev];
      next[index] = clean.slice(0, 1);
      return next;
    });

    if (clean && index < 5) setTimeout(() => inputRefs.current[index + 1]?.focus(), 0);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        next[index - 1] = "";
        return next;
      });
      setTimeout(() => inputRefs.current[index - 1]?.focus(), 0);
    }

    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    const clean = pasted.replace(/\D/g, "").slice(0, 6);
    if (!clean) return;

    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    clean.split("").forEach((char, index) => {
      next[index] = char;
    });

    setDigits(next);
    setTimeout(() => inputRefs.current[Math.min(clean.length, 6) - 1]?.focus(), 0);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setNotice("");

    if (!uid) {
      setError("No encontramos tu registro pendiente. Vuelve a crear tu cuenta.");
      return;
    }

    if (!/^\d{6}$/.test(code)) {
      setError("Ingresa el código de 6 dígitos que recibiste por correo.");
      return;
    }

    setLoading(true);

    try {
      let data = null;

      try {
        data = await withTimeout(
          httpRequestJson({
            url: LOOP_VERIFY_CODE_HTTP_URL,
            method: "POST",
            data: { uid, code, email, source: isIosCapacitor() ? "ios_enter_code_v29" : "web_enter_code_v29" },
          }),
          35000,
          "verify-code-http-timeout"
        );
      } catch (httpErr) {
        if (isIosCapacitor()) throw httpErr;
        console.warn("[EnterCode V29] HTTP verify failed on web; falling back to callable", httpErr);
        const functions = getFunctions();
        const verifyFn = httpsCallable(functions, "verifyRegistrationCode");
        const resp = await withTimeout(verifyFn({ uid, code }), 30000, "verify-code-callable-timeout");
        data = resp?.data || null;
      }

      if (data?.ok) {
        await finishVerifiedSession({
          customToken: data.customToken || "",
          verifiedEmail: data.email || email,
          displayName: data.displayName || data.fullName || "",
        });
        return;
      }

      setError("No pudimos verificar el código. Intenta nuevamente.");
    } catch (err) {
      console.error("verify call error", err);
      setError(codeFlowErrorMessage(err, "No pudimos verificar el código. Intenta nuevamente."));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setNotice("");

    if (!uid) {
      setError("No encontramos tu registro pendiente. Vuelve a crear tu cuenta.");
      return;
    }

    if (!email) {
      setError("No encontramos el correo de registro. Vuelve a crear tu cuenta.");
      return;
    }

    setResending(true);

    try {
      try {
        await withTimeout(
          httpRequestJson({
            url: LOOP_RESEND_CODE_HTTP_URL,
            method: "POST",
            data: { uid, email, source: isIosCapacitor() ? "ios_enter_code_resend_v29" : "web_enter_code_resend_v29" },
          }),
          35000,
          "resend-code-http-timeout"
        );
      } catch (httpErr) {
        if (isIosCapacitor()) throw httpErr;
        console.warn("[EnterCode V29] HTTP resend failed on web; falling back to callable", httpErr);
        const functions = getFunctions();
        const sendFn = httpsCallable(functions, "sendVerificationCode");
        await withTimeout(sendFn({ uid, email }), 30000, "resend-code-callable-timeout");
      }

      setNotice("Te enviamos un nuevo código. Revisa tu correo y la carpeta de spam.");
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    } catch (err) {
      console.error("resend error", err);
      setError(codeFlowErrorMessage(err, "No pudimos reenviar el código. Intenta más tarde."));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="loop-code-page">
      <style>{`
        .loop-code-page {
          min-height: calc(100vh - 80px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 34px 16px 54px;
          background:
            radial-gradient(circle at 18% 8%, rgba(0,103,95,.11), transparent 30%),
            radial-gradient(circle at 82% 18%, rgba(20,184,166,.10), transparent 30%),
            linear-gradient(180deg, #f8fafc 0%, #ffffff 56%, #f8fafc 100%);
        }

        .loop-code-shell {
          width: 100%;
          max-width: 520px;
        }

        .loop-code-card {
          position: relative;
          overflow: hidden;
          border-radius: 34px;
          border: 1px solid rgba(226,232,240,.95);
          background: rgba(255,255,255,.96);
          box-shadow: 0 26px 80px rgba(15,23,42,.12);
        }

        .loop-code-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at top left, rgba(0,103,95,.13), transparent 34%),
            linear-gradient(180deg, rgba(255,255,255,.2), transparent 45%);
          pointer-events: none;
        }

        .loop-code-inner {
          position: relative;
          padding: 34px 30px 30px;
        }

        .loop-code-badge {
          width: 58px;
          height: 58px;
          border-radius: 22px;
          display: grid;
          place-items: center;
          margin: 0 auto 18px;
          background: #ecfdf5;
          border: 1px solid rgba(153,246,228,.9);
          color: #00675f;
          box-shadow: 0 16px 34px rgba(0,103,95,.13);
        }

        .loop-code-kicker {
          margin: 0;
          text-align: center;
          color: #00675f;
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .16em;
          text-transform: uppercase;
        }

        .loop-code-title {
          margin: 8px 0 0;
          text-align: center;
          color: #07111f;
          font-size: clamp(30px, 5vw, 42px);
          line-height: .96;
          letter-spacing: -.065em;
          font-weight: 950;
        }

        .loop-code-subtitle {
          margin: 13px auto 0;
          max-width: 390px;
          color: #64748b;
          text-align: center;
          font-size: 14px;
          line-height: 1.55;
          font-weight: 720;
        }

        .loop-code-subtitle strong {
          color: #0f172a;
          font-weight: 950;
        }

        .loop-code-form {
          margin-top: 26px;
        }

        .loop-code-digits {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 10px;
        }

        .loop-code-input {
          width: 100%;
          height: 58px;
          border-radius: 18px;
          border: 1px solid #dbe4ef;
          background: #ffffff;
          color: #07111f;
          font-size: 25px;
          font-weight: 950;
          text-align: center;
          outline: none;
          box-shadow: 0 8px 20px rgba(15,23,42,.04);
          transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
        }

        .loop-code-input:focus {
          border-color: #00a693;
          box-shadow: 0 0 0 4px rgba(0,166,147,.13), 0 12px 26px rgba(15,23,42,.06);
          transform: translateY(-1px);
        }

        .loop-code-alert {
          margin-top: 16px;
          border-radius: 18px;
          padding: 13px 14px;
          font-size: 13px;
          line-height: 1.45;
          font-weight: 780;
        }

        .loop-code-alert.error {
          color: #b91c1c;
          border: 1px solid #fecaca;
          background: #fef2f2;
        }

        .loop-code-alert.notice {
          color: #047857;
          border: 1px solid #a7f3d0;
          background: #ecfdf5;
        }

        .loop-code-primary {
          margin-top: 18px;
          width: 100%;
          min-height: 56px;
          border: 0;
          border-radius: 20px;
          background: linear-gradient(135deg, #00675f, #009982);
          color: #ffffff;
          font-size: 15px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 18px 36px rgba(0,103,95,.24);
          transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
        }

        .loop-code-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 22px 44px rgba(0,103,95,.28);
        }

        .loop-code-primary:disabled {
          cursor: not-allowed;
          opacity: .62;
          transform: none;
        }

        .loop-code-secondary-row {
          margin-top: 16px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          justify-content: center;
          color: #64748b;
          font-size: 13px;
          font-weight: 720;
        }

        .loop-code-link {
          border: 0;
          background: transparent;
          color: #00675f;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
          padding: 0;
        }

        .loop-code-link:disabled {
          cursor: not-allowed;
          opacity: .55;
        }

        .loop-code-footnote {
          margin: 20px auto 0;
          max-width: 360px;
          text-align: center;
          color: #94a3b8;
          font-size: 12px;
          line-height: 1.55;
          font-weight: 650;
        }

        .loop-code-back {
          margin-top: 16px;
          width: 100%;
          min-height: 48px;
          border-radius: 18px;
          border: 1px solid #e2e8f0;
          background: rgba(255,255,255,.76);
          color: #334155;
          font-size: 14px;
          font-weight: 900;
          cursor: pointer;
        }

        @media (max-width: 520px) {
          .loop-code-page {
            align-items: flex-start;
            padding-top: 22px;
          }

          .loop-code-inner {
            padding: 28px 18px 22px;
          }

          .loop-code-card {
            border-radius: 28px;
          }

          .loop-code-digits {
            gap: 7px;
          }

          .loop-code-input {
            height: 52px;
            border-radius: 15px;
            font-size: 22px;
          }
        }
      `}</style>

      <div className="loop-code-shell">
        <div className="loop-code-card">
          <div className="loop-code-inner">
            <div className="loop-code-badge" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M4.5 8.5L11.1 13.05C11.65 13.43 12.35 13.43 12.9 13.05L19.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5.5 6.5H18.5C19.6 6.5 20.5 7.4 20.5 8.5V17.5C20.5 18.6 19.6 19.5 18.5 19.5H5.5C4.4 19.5 3.5 18.6 3.5 17.5V8.5C3.5 7.4 4.4 6.5 5.5 6.5Z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>

            <p className="loop-code-kicker">Verificación segura</p>
            <h1 className="loop-code-title">Revisa tu correo</h1>

            <p className="loop-code-subtitle">
              Enviamos un código de 6 dígitos
              {maskedEmail ? <> a <strong>{maskedEmail}</strong>.</> : "."}
              {" "}Ingésalo para activar tu cuenta en Loop.
            </p>

            <form onSubmit={handleSubmit} className="loop-code-form">
              <div className="loop-code-digits" onPaste={handlePaste}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el; }}
                    className="loop-code-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => updateDigit(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    aria-label={`Dígito ${index + 1}`}
                    disabled={loading}
                  />
                ))}
              </div>

              {error ? <div className="loop-code-alert error">{error}</div> : null}
              {notice ? <div className="loop-code-alert notice">{notice}</div> : null}

              <button type="submit" className="loop-code-primary" disabled={loading || resending}>
                {loading ? "Verificando..." : "Verificar y continuar"}
              </button>

              <div className="loop-code-secondary-row">
                <span>¿No te llegó?</span>
                <button type="button" className="loop-code-link" onClick={handleResend} disabled={loading || resending}>
                  {resending ? "Reenviando..." : "Reenviar código"}
                </button>
              </div>

              <p className="loop-code-footnote">
                Si no lo ves en tu bandeja principal, revisa spam o correos no deseados.
              </p>

              <button type="button" className="loop-code-back" onClick={() => navigate("/login")} disabled={loading || resending}>
                Volver a iniciar sesión
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
